import { XIcon } from '@phosphor-icons/react';
import { compareDesc, parseISO } from 'date-fns';
import { css, html } from 'react-strict-dom';
import { useForm } from 'react-typed-form';

import { Api, useApiCallable, useApiFormSubmit } from '../../../api';
import { Avatar, Stack, Text } from '../../../components';
import { Color, Style } from '../../../constants';
import { FieldText, FormWrap, SubmitButton } from '../../../form';
import {
  formatAsDateTime,
  formatTimeSpent,
  parseTimeSpent,
} from '../../../util';

type FormValues = Omit<Api.WorkDone.W, 'minutes'> & { minutes: string | null };

type Props = Readonly<{
  defaultValues: Partial<FormValues>;
  workDones: Array<{
    ['@id']: string;
    user: Api.User.BaseView;
    minutes: number;
    loggedAt: string;
  }>;
  invalidate: () => unknown;
}>;

export default function WorkDoneDropdown({
  defaultValues,
  workDones,
  invalidate,
}: Props) {
  const callApi = useApiCallable();
  const formSubmit = useApiFormSubmit('POST', '/work-dones', { invalidate });

  const form = useForm<Partial<FormValues>>({
    defaultValues,
    onSubmit: (values, submittedForm) =>
      formSubmit(
        { ...values, minutes: parseTimeSpent(values.minutes ?? '') },
        submittedForm
      ),
  });

  const sorted = [...workDones].sort((a, b) =>
    compareDesc(parseISO(a.loggedAt), parseISO(b.loggedAt))
  );

  return (
    <html.div style={styles.container} onClick={(ev) => ev.stopPropagation()}>
      <Stack gap={1} style={styles.workDonesList}>
        {sorted.map((workDone) => (
          <Stack
            key={workDone['@id']}
            style={[Style.row, Style.alignCenter, Style.spaceBetween]}
          >
            <Stack style={[Style.row, Style.alignCenter]} gap={0.5}>
              <Avatar user={workDone.user} size={32} />
              <Text typeface="body.medium" color="neutral100">
                {formatTimeSpent(workDone.minutes)}
              </Text>
            </Stack>
            <Stack style={[Style.row, Style.alignCenter]} gap={0.5}>
              <Text typeface="body.small.regular" color="neutral80">
                {formatAsDateTime(workDone.loggedAt)}
              </Text>
              <html.button
                style={styles.deleteButton}
                onClick={async () => {
                  if (confirm('Really delete?')) {
                    await callApi(workDone['@id'], { method: 'DELETE' });
                    invalidate();
                  }
                }}
              >
                <XIcon size={24} color={Color.neutral60} />
              </html.button>
            </Stack>
          </Stack>
        ))}
      </Stack>

      <Stack padV={0.5}>
        <FormWrap
          formErrorList={form.formErrorList}
          handleSubmit={form.handleSubmit}
        >
          <Stack style={[Style.row, Style.alignCenter]} gap={1}>
            <html.div style={Style.fill}>
              <FieldText
                field={form.getField('minutes')}
                label={null}
                placeholder="Minutes"
              />
            </html.div>
            <SubmitButton {...form} label="Submit" />
          </Stack>
        </FormWrap>
      </Stack>
    </html.div>
  );
}

const styles = css.create({
  container: {
    width: 369,
    paddingInline: 24,
    paddingBlock: 20,
  },
  workDonesList: {
    marginBottom: 16,
  },
  deleteButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 0,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    padding: 0,
  },
});
